import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, Typography } from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../myTrack/AuthContext';
import { rootUrl } from '../../config/config';

export default function SystemWeeksPage() {
    const { token } = useAuth();
    const authToken = token || localStorage.getItem('authToken') || '';
    const [weeks, setWeeks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchWeeks = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await axios.get(`${rootUrl}/system/weeks`, {
                    headers: { Authorization: `Bearer ${authToken}` },
                });
                const data = response.data?.data ?? response.data ?? [];
                setWeeks(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('Error fetching system weeks:', err);
                setError(err.response?.data?.message || 'Failed to load system weeks');
                setWeeks([]);
            } finally {
                setLoading(false);
            }
        };
        fetchWeeks();
    }, [authToken]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <FontAwesomeIcon icon={faSpinner} spin className="text-3xl text-[#FF5733] mr-3" />
                <Typography className="text-gray-600">Loading system weeks...</Typography>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-[#2a2e30] mb-2">System Weeks</h1>
                <p className="text-gray-600">View-only list of system weeks used for reporting and transactions.</p>
            </div>

            {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    {error}
                </div>
            )}

            <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[640px]">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="text-left py-4 px-4 text-sm font-semibold text-gray-700">Week</th>
                                <th className="text-left py-4 px-4 text-sm font-semibold text-gray-700">Year</th>
                                <th className="text-left py-4 px-4 text-sm font-semibold text-gray-700">Week #</th>
                                <th className="text-left py-4 px-4 text-sm font-semibold text-gray-700">Start Date</th>
                                <th className="text-left py-4 px-4 text-sm font-semibold text-gray-700">End Date</th>
                                <th className="text-left py-4 px-4 text-sm font-semibold text-gray-700">Closed</th>
                            </tr>
                        </thead>
                        <tbody>
                            {weeks.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="py-12 text-center text-gray-500">
                                        No system weeks found.
                                    </td>
                                </tr>
                            ) : (
                                weeks.map((w) => (
                                    <tr key={w.id || `${w.year}_${w.week_number}`} className="border-b border-gray-100 hover:bg-gray-50">
                                        <td className="py-3 px-4 font-semibold text-[#2a2e30]">{w.week || '-'}</td>
                                        <td className="py-3 px-4 text-gray-700">{w.year ?? '-'}</td>
                                        <td className="py-3 px-4 text-gray-700">{w.week_number ?? '-'}</td>
                                        <td className="py-3 px-4 text-gray-700">{formatDate(w.start_date)}</td>
                                        <td className="py-3 px-4 text-gray-700">{formatDate(w.end_date)}</td>
                                        <td className="py-3 px-4">{w.is_closed ? 'Yes' : 'No'}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>
        </div>
    );
}
